'use client'

/**
 * Chat Window Component
 *
 * The main chat interface with message list, input, and controls.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { X, Send, Loader2, RefreshCw, Bot, UserCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useChatSession } from './hooks/useChatSession'
import { useStreamingResponse } from './hooks/useStreamingResponse'
import { MessageBubble } from './MessageBubble'
import { TypingIndicator } from './TypingIndicator'
import type { ChatWindowProps } from './types'

// Welcome message based on property context
function getWelcomeMessage(
  propertyName: string | null | undefined,
  tenantName: string
): string {
  if (propertyName) {
    return `Welcome to ${propertyName}! I'm your booking assistant. How can I help you today? You can ask me about room availability, prices, or help you make a reservation.`
  }
  return `Welcome to ${tenantName}! I'm your booking assistant. I can help you find available properties, check room availability, and make reservations. What dates are you looking to book?`
}

export function ChatWindow({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  tenantLogo,
  propertyId,
  propertyName,
}: ChatWindowProps) {
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const hasInitializedRef = useRef(false)

  const {
    session,
    messages,
    isLoading: isSessionLoading,
    isStaffHandling,
    error: sessionError,
    initSession,
    addUserMessage,
    addAssistantMessage,
    clearError,
  } = useChatSession(tenantId)

  const handleFunctionCall = useCallback(
    (_name: string, _result?: unknown) => {
      // Could show function execution status in UI
    },
    []
  )

  const {
    isProcessing,
    error: streamError,
    sendMessage,
  } = useStreamingResponse(handleFunctionCall)

  // Initialize session when opened (only once per window open)
  useEffect(() => {
    if (isOpen && !session && !isSessionLoading && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      initSession(tenantId, propertyId)
    }
    // Reset the flag when window is closed
    if (!isOpen) {
      hasInitializedRef.current = false
    }
  }, [isOpen, session, isSessionLoading, tenantId, propertyId, initSession])

  // Scroll to bottom on new messages or when processing
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isProcessing])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && session) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen, session])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedInput = inputValue.trim()
    if (!trimmedInput || !session || isProcessing) return

    // Add user message to UI immediately for responsiveness
    // Note: The subscription will also receive this, but we add it locally
    // for immediate feedback. The subscription will update with the real ID.
    addUserMessage(trimmedInput)
    setInputValue('')

    // Send to AI engine and add response to UI immediately
    const aiResponse = await sendMessage(session.sessionCode, trimmedInput)
    if (aiResponse) {
      addAssistantMessage(aiResponse)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleRetry = () => {
    clearError()
    if (!session) {
      initSession(tenantId, propertyId)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed bottom-20 right-4 z-50 flex h-[min(500px,calc(100vh-6rem))] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border bg-background shadow-xl lg:bottom-6 sm:right-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-primary px-4 py-3 text-primary-foreground">
        <div className="flex items-center gap-2">
          {tenantLogo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={tenantLogo}
              alt={tenantName}
              className="h-8 w-8 rounded-full bg-white object-contain p-0.5"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-foreground/10">
              <Bot className="h-5 w-5" />
            </div>
          )}
          <div>
            <h3 className="text-sm font-semibold">{tenantName}</h3>
            <p className="text-xs opacity-80">Booking Assistant</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {isSessionLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : sessionError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-destructive">{sessionError}</p>
            <Button variant="outline" size="sm" onClick={handleRetry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Welcome message */}
            {messages.length === 0 && !isProcessing && (
              <MessageBubble
                role="assistant"
                content={getWelcomeMessage(propertyName, tenantName)}
              />
            )}

            {/* Chat messages - filter out function messages and raw JSON */}
            {messages
              .filter((msg) => {
                // Hide function role messages (these are internal tool results)
                // API can deliver 'function' role messages beyond the ChatMessage union
                if ((msg.role as string) === 'function' || msg.role === 'system') return false
                // Hide assistant messages that are just JSON (function call artifacts)
                if (msg.role === 'assistant' && msg.content) {
                  const trimmed = msg.content.trim()
                  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                    try {
                      JSON.parse(trimmed)
                      return false // It's valid JSON, hide it
                    } catch {
                      // Not valid JSON, show it
                    }
                  }
                }
                // Hide empty messages
                if (!msg.content || msg.content.trim() === '') return false
                return true
              })
              .map((msg) => (
                <MessageBubble
                  key={msg.id}
                  role={msg.role}
                  content={msg.content}
                  timestamp={msg.createdAt}
                  senderType={msg.senderType}
                  staffName={msg.staffName}
                />
              ))}

            {/* Typing indicator while processing */}
            {isProcessing && <TypingIndicator />}

            {/* Staff handling indicator */}
            {isStaffHandling && !isProcessing && (
              <div className="flex items-center gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-sm text-blue-700 dark:text-blue-300">
                <UserCheck className="h-4 w-4" />
                <span>A staff member is responding...</span>
              </div>
            )}

            {/* Error message */}
            {streamError && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {streamError}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t p-3">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            disabled={!session || isProcessing}
            className={cn(
              'flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm',
              'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'max-h-24 min-h-[40px]'
            )}
            rows={1}
          />
          <Button
            type="submit"
            size="icon"
            disabled={!inputValue.trim() || !session || isProcessing}
            className="h-10 w-10 shrink-0"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="mt-2 text-center text-[10px] text-muted-foreground">
          Powered by AI - May produce inaccurate information
        </p>
      </form>
    </div>
  )
}
